// ============= STRIPE PAYMENTS & PRICING UI (Inclusion+AI v3.3) =============
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, CreditCard, Sparkles, Award, Crown, Building2, GraduationCap, Tag, Calendar, Plus, Trash2, Pencil, AlertCircle, ArrowRight, ShieldCheck, RefreshCw, TrendingUp, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ====== Shared atoms (mirrored from App.js to keep Payments self-contained) ======
const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-white rounded-xl border border-[#E5E2DB] p-6 shadow-sm ${className}`} {...rest}>{children}</div>
);
const Btn = ({ children, variant = "primary", className = "", ...p }) => {
  const cls = {
    primary: "bg-[#2F5233] text-white hover:bg-[#244126]",
    outline: "bg-white text-[#2F5233] border border-[#2F5233] hover:bg-[#F0EFEB]",
    ghost: "bg-transparent text-[#4A5553] hover:bg-[#F0EFEB]",
    accent: "bg-[#769FCD] text-white hover:bg-[#5e87b5]",
    danger: "bg-red-500 text-white hover:bg-red-600",
    gold: "bg-gradient-to-l from-[#E8A317] to-[#d68f00] text-white hover:opacity-90",
  }[variant];
  return <button className={`px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 ${cls} ${className}`} {...p}>{children}</button>;
};
const Input = (p) => <input {...p} className={`w-full px-4 py-2.5 rounded-lg border border-[#E5E2DB] focus:border-[#2F5233] outline-none ${p.className||""}`} />;
const Select = (p) => <select {...p} className={`w-full px-4 py-2.5 rounded-lg border border-[#E5E2DB] focus:border-[#2F5233] outline-none bg-white ${p.className||""}`}>{p.children}</select>;
const fmtErr = (d) => typeof d === "string" ? d : (Array.isArray(d) ? d.map(e => e.msg || e).join(" · ") : "حدث خطأ");

const TIER_META = {
  free: { icon: ShieldCheck, color: "text-[#4A5553]", bg: "bg-[#F0EFEB]" },
  parent_pro: { icon: Crown, color: "text-emerald-700", bg: "bg-emerald-100" },
  specialist: { icon: GraduationCap, color: "text-[#527598]", bg: "bg-[#769FCD]/15" },
  school: { icon: Building2, color: "text-[#A36F00]", bg: "bg-[#E8A317]/15" },
  center: { icon: Building2, color: "text-[#7A4B0F]", bg: "bg-amber-100" },
  addon: { icon: Sparkles, color: "text-rose-600", bg: "bg-rose-100" },
  accreditation: { icon: Award, color: "text-purple-700", bg: "bg-purple-100" },
  training: { icon: GraduationCap, color: "text-[#527598]", bg: "bg-blue-100" },
};

const CURRENCY_SYMBOL = { sar: "ر.س", usd: "$", aed: "د.إ" };

function fmtMoney(amount, currency = "sar") {
  if (amount == null || isNaN(amount)) return "—";
  const sym = CURRENCY_SYMBOL[currency?.toLowerCase()] || currency;
  return `${Number(amount).toLocaleString("ar-EG", { minimumFractionDigits: amount % 1 ? 2 : 0 })} ${sym}`;
}

function intervalLabel(interval) {
  if (interval === "month") return "شهرياً";
  if (interval === "year") return "سنوياً";
  return "دفعة واحدة";
}

// ============= /pricing =============
export function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [audience, setAudience] = useState("specialist");
  const [loading, setLoading] = useState(true);
  const [busyPlanId, setBusyPlanId] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponPreview, setCouponPreview] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/pricing/plans`).then(r => setPlans(r.data)).finally(() => setLoading(false));
  }, []);
  const audiences = [
    { v: "specialist", l: "المعلمون والأخصائيون", I: GraduationCap },
    { v: "school", l: "المدارس", I: Building2 },
    { v: "center", l: "المراكز التخصصية", I: Building2 },
  ];
  const subs = plans.filter(p => p.type === "subscription" && (p.audience || []).includes(audience));
  const addons = plans.filter(p => p.type === "one_time");

  const subscribe = async (plan) => {
    if (plan.amount <= 0) { nav("/register"); return; }
    setBusyPlanId(plan.id);
    try {
      const token = localStorage.getItem("token");
      if (!token) { nav(`/login?next=/pricing`); return; }
      const { data } = await axios.post(`${API}/payments/checkout`, {
        plan_id: plan.id, origin_url: window.location.origin,
        coupon_code: couponPreview && couponPreview.plan_id === plan.id ? coupon : null,
      });
      window.location.href = data.url;
    } catch (e) { alert(fmtErr(e.response?.data?.detail)); }
    finally { setBusyPlanId(null); }
  };

  const previewCoupon = async (plan) => {
    if (!coupon.trim()) return;
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code: coupon.trim(), plan_id: plan.id,
      });
      setCouponPreview({ ...data, plan_id: plan.id });
    } catch (e) { alert(fmtErr(e.response?.data?.detail) || "كوبون غير صالح"); }
  };

  return <div className="min-h-screen p-6 md:p-10" dir="rtl" style={{ background: "linear-gradient(135deg,#F9F8F6,#F0EFEB)" }}>
    <div className="max-w-6xl mx-auto" data-testid="pricing-page">
      {/* HERO */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 text-xs text-[#2F5233] font-semibold tracking-wider uppercase mb-2"><Sparkles size={14}/>الباقات والأسعار</div>
        <h1 className="text-4xl md:text-5xl font-bold font-heading">منصة التعليم الشامل — للمعلمين والمدارس والمراكز</h1>
        <p className="text-[#4A5553] mt-3 max-w-2xl mx-auto">أولياء الأمور <strong>يستخدمون المنصة مجاناً</strong> عند ربط أبنائهم بمدرسة أو معلم مشترك. الأسعار أدناه للمؤسسات التعليمية والكوادر المتخصصة.</p>
      </div>

      {/* Audience tabs */}
      <div className="flex justify-center gap-2 mb-6" data-testid="pricing-tabs">
        {audiences.map(a => <button key={a.v} onClick={()=>setAudience(a.v)} data-testid={`tab-${a.v}`}
          className={`px-5 py-2.5 rounded-full text-sm font-medium transition inline-flex items-center gap-2 ${audience===a.v?"bg-[#2F5233] text-white shadow":"bg-white text-[#4A5553] hover:bg-[#F0EFEB] border border-[#E5E2DB]"}`}>
          <a.I size={14}/>{a.l}
        </button>)}
      </div>

      {/* Plans grid */}
      {loading ? <div className="text-center py-12 text-[#4A5553]">جارٍ التحميل...</div> :
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {subs.map(plan => {
          const meta = TIER_META[plan.tier] || TIER_META.free;
          const Icon = meta.icon;
          const showPreview = couponPreview && couponPreview.plan_id === plan.id;
          return <div key={plan.id} className={`relative rounded-2xl border-2 p-6 transition flex flex-col ${plan.popular?"border-[#2F5233] shadow-lg scale-[1.02] bg-white":"border-[#E5E2DB] bg-white hover:border-[#2F5233]/40"}`} data-testid={`plan-${plan.slug}`}>
            {plan.popular && <span className="absolute -top-3 right-1/2 translate-x-1/2 px-3 py-0.5 text-xs font-bold rounded-full bg-[#2F5233] text-white shadow">الأكثر شيوعاً ⭐</span>}
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-10 h-10 rounded-xl ${meta.bg} ${meta.color} flex items-center justify-center`}><Icon size={20}/></div>
              <div>
                <h3 className="font-bold text-lg font-heading">{plan.name_ar}</h3>
                <p className="text-[10px] text-[#4A5553]">{intervalLabel(plan.interval)}</p>
              </div>
            </div>
            <div className="mb-4">
              {showPreview ? <div data-testid={`preview-${plan.slug}`}>
                <p className="text-sm text-[#4A5553] line-through">{fmtMoney(plan.amount, plan.currency)}</p>
                <p className="text-3xl font-bold text-emerald-700">{fmtMoney(couponPreview.discounted_amount, plan.currency)}</p>
                <p className="text-[10px] text-emerald-600">وفّر {fmtMoney(couponPreview.savings, plan.currency)} مع {coupon.toUpperCase()}</p>
              </div> : <p className="text-3xl font-bold">{plan.amount === 0 ? "مجاناً" : fmtMoney(plan.amount, plan.currency)}</p>}
              <p className="text-[10px] text-[#4A5553] mt-1">{plan.interval ? `/${plan.interval === "month" ? "شهر" : "سنة"}` : ""}</p>
            </div>
            <ul className="space-y-2 mb-5 flex-1">
              {(plan.features_ar || []).map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check size={14} className="text-emerald-600 flex-shrink-0 mt-0.5"/><span>{f}</span></li>)}
            </ul>
            {plan.amount > 0 && <div className="space-y-2 mb-3">
              <div className="flex gap-1.5">
                <Input placeholder="كوبون خصم (اختياري)" value={coupon} onChange={e=>setCoupon(e.target.value.toUpperCase())} className="flex-1 text-xs py-1.5" data-testid={`coupon-input-${plan.slug}`}/>
                <Btn variant="ghost" onClick={()=>previewCoupon(plan)} disabled={!coupon.trim()} className="text-xs">تطبيق</Btn>
              </div>
            </div>}
            <Btn variant={plan.popular ? "gold" : "primary"} onClick={()=>subscribe(plan)} disabled={busyPlanId===plan.id} className="w-full" data-testid={`subscribe-${plan.slug}`}>
              {busyPlanId === plan.id ? "..." : plan.amount === 0 ? "ابدأ مجاناً" : "اشترك الآن"}
            </Btn>
          </div>;
        })}
      </div>}

      {/* One-time add-ons */}
      {addons.length > 0 && <div className="mt-10">
        <h2 className="text-2xl font-bold font-heading mb-4 flex items-center gap-2"><Sparkles size={20} className="text-rose-600"/>إضافات بدفعة واحدة</h2>
        <p className="text-sm text-[#4A5553] mb-4">شراء لمرة واحدة لخدمات مميزة دون اشتراك دوري.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {addons.map(plan => <div key={plan.id} className="rounded-xl border-2 border-rose-200 bg-gradient-to-tl from-rose-50/60 to-white p-4" data-testid={`addon-${plan.slug}`}>
            <div className="flex items-center gap-2 mb-2"><Sparkles size={18} className="text-rose-600"/><h3 className="font-semibold text-sm">{plan.name_ar}</h3></div>
            <p className="text-2xl font-bold mb-2">{fmtMoney(plan.amount, plan.currency)}</p>
            <ul className="space-y-1 mb-3 text-xs">
              {(plan.features_ar || []).slice(0, 3).map((f, i) => <li key={i} className="flex gap-1.5"><Check size={12} className="text-emerald-600 flex-shrink-0 mt-0.5"/><span>{f}</span></li>)}
            </ul>
            <Btn variant="accent" onClick={()=>subscribe(plan)} disabled={busyPlanId===plan.id} className="w-full text-xs">{busyPlanId===plan.id?"...":"شراء"}</Btn>
          </div>)}
        </div>
      </div>}

      <div className="text-center mt-10 text-xs text-[#4A5553]">
        <p>الأسعار تشمل كل الميزات الموضّحة. يمكن إلغاء الاشتراك في أي وقت من صفحة الفوترة.</p>
        <p className="mt-1">دفع آمن عبر <strong>Stripe</strong> · معالجة فورية · إيصال PDF عبر البريد.</p>
      </div>
    </div>
  </div>;
}

// ============= /checkout/success =============
export function CheckoutSuccess() {
  const [status, setStatus] = useState("checking");
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setErr("معرّف الجلسة مفقود"); setStatus("error"); return; }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        setTx(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        attempts++;
        if (attempts < max) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch (e) {
        setErr(fmtErr(e.response?.data?.detail));
        setStatus("error");
      }
    };
    poll();
  }, []);

  return <div className="min-h-screen flex items-center justify-center p-6" dir="rtl" style={{ background: "linear-gradient(135deg,#F9F8F6,#F0EFEB)" }}>
    <Card className="w-full max-w-lg" data-testid="checkout-success-page">
      {status === "checking" && <div className="text-center py-10">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#769FCD]/15 flex items-center justify-center animate-pulse"><RefreshCw className="text-[#769FCD] animate-spin" size={28}/></div>
        <h2 className="text-xl font-bold font-heading mb-1">جارٍ التحقق من دفعتك...</h2>
        <p className="text-sm text-[#4A5553]">قد يستغرق ذلك بضع ثوانٍ.</p>
      </div>}
      {status === "paid" && tx && <div className="text-center py-6" data-testid="checkout-paid">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="text-emerald-600" size={32}/></div>
        <h2 className="text-2xl font-bold font-heading text-emerald-700 mb-2">تم الدفع بنجاح 🎉</h2>
        <p className="text-sm text-[#4A5553] mb-4">شكراً لاشتراكك في <strong>{tx.plan_name_ar}</strong>.</p>
        <div className="bg-[#F9F8F6] rounded-lg p-3 mb-4 text-xs text-right">
          <div className="flex justify-between mb-1"><span>المبلغ المدفوع:</span><strong>{fmtMoney(tx.amount, tx.currency)}</strong></div>
          {tx.coupon_code && <div className="flex justify-between mb-1 text-emerald-700"><span>كوبون مُطبَّق:</span><strong>{tx.coupon_code}</strong></div>}
          <div className="flex justify-between text-[10px] text-[#4A5553]"><span>معرّف الفاتورة:</span><span className="font-mono">{tx.session_id?.slice(-12)}</span></div>
        </div>
        <div className="flex gap-2 justify-center">
          <Link to="/billing"><Btn data-testid="goto-billing">عرض الفوترة</Btn></Link>
          <Link to="/"><Btn variant="outline">العودة للوحة</Btn></Link>
        </div>
      </div>}
      {status === "expired" && <div className="text-center py-6">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center"><AlertCircle className="text-amber-600" size={28}/></div>
        <h2 className="text-lg font-bold mb-2">انتهت جلسة الدفع</h2>
        <p className="text-sm text-[#4A5553] mb-4">حاول مجدداً من صفحة الباقات.</p>
        <Link to="/pricing"><Btn>الباقات</Btn></Link>
      </div>}
      {status === "timeout" && <div className="text-center py-6">
        <p className="text-sm text-[#4A5553] mb-3">جلسة الدفع لا تزال قيد المعالجة. ستظهر النتيجة في صفحة الفوترة خلال دقائق.</p>
        <Link to="/billing"><Btn>عرض الفوترة</Btn></Link>
      </div>}
      {status === "error" && <div className="text-center py-6">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center"><X className="text-red-600" size={28}/></div>
        <h2 className="text-lg font-bold mb-2">حدث خطأ</h2>
        <p className="text-sm text-red-600 mb-3">{err}</p>
        <Link to="/pricing"><Btn variant="outline">العودة</Btn></Link>
      </div>}
    </Card>
  </div>;
}

// ============= /checkout/cancelled =============
export function CheckoutCancelled() {
  return <div className="min-h-screen flex items-center justify-center p-6" dir="rtl" style={{ background: "linear-gradient(135deg,#F9F8F6,#F0EFEB)" }}>
    <Card className="w-full max-w-md text-center" data-testid="checkout-cancelled-page">
      <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center"><AlertCircle className="text-amber-600" size={28}/></div>
      <h2 className="text-xl font-bold mb-2">تم إلغاء الدفع</h2>
      <p className="text-sm text-[#4A5553] mb-4">لم يتم خصم أي مبلغ. يمكنك المحاولة مجدداً متى شئت.</p>
      <Link to="/pricing"><Btn>العودة للباقات</Btn></Link>
    </Card>
  </div>;
}

// ============= /billing =============
function PortalButton() {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/portal`, {});
      window.location.href = data.url;
    } catch (e) {
      alert(fmtErr(e.response?.data?.detail) || "تعذّر فتح بوابة الإدارة");
    } finally { setBusy(false); }
  };
  return <Btn variant="primary" onClick={open} disabled={busy} data-testid="manage-subscription"><RefreshCw size={14} className="inline ms-1"/>{busy?"جارٍ الفتح...":"إدارة الاشتراك"}</Btn>;
}

export function Billing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/payments/me`).then(r => setData(r.data)).finally(()=>setLoading(false));
  }, []);
  if (loading) return <div className="p-8 text-center text-[#4A5553]" data-testid="billing-loading">جارٍ التحميل...</div>;
  if (!data) return null;
  const { active_subscription, active_plan, transactions, invoices } = data;
  return <div className="space-y-6 fade-up" data-testid="billing-page">
    <div>
      <div className="flex items-center gap-2 text-xs text-[#2F5233] font-semibold uppercase tracking-wider mb-1"><CreditCard size={14}/>الفوترة والاشتراكات</div>
      <h1 className="text-3xl font-bold font-heading">الفوترة الخاصة بك</h1>
      <p className="text-sm text-[#4A5553] mt-1">اشتراكك الحالي، سجل المدفوعات، والفواتير الرسمية.</p>
    </div>

    {/* Active subscription */}
    {active_plan ? <Card className="border-2 border-emerald-300 bg-gradient-to-tl from-emerald-50/30 to-white" data-testid="active-subscription-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0"><Crown size={22}/></div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-emerald-700 font-semibold">الاشتراك النشط</p>
          <h3 className="text-xl font-bold font-heading">{active_plan.name_ar}</h3>
          <p className="text-sm text-[#4A5553] mt-1">{intervalLabel(active_plan.interval)} · {fmtMoney(active_plan.amount, active_plan.currency)}</p>
          {active_subscription?.expires_at && <p className="text-xs text-[#4A5553] mt-2">📅 يتجدد / ينتهي في: <strong>{new Date(active_subscription.expires_at).toLocaleDateString("ar-EG")}</strong></p>}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <PortalButton/>
          <Link to="/pricing"><Btn variant="outline" data-testid="change-plan">تغيير الباقة</Btn></Link>
        </div>
      </div>
    </Card> : <Card data-testid="no-subscription">
      <div className="text-center py-6">
        <Crown size={32} className="mx-auto mb-2 text-[#4A5553]"/>
        <p className="font-semibold mb-1">لا يوجد اشتراك نشط</p>
        <p className="text-xs text-[#4A5553] mb-3">ارتقِ بتجربتك مع باقة Pro للحصول على ميزات أكثر.</p>
        <Link to="/pricing"><Btn>عرض الباقات</Btn></Link>
      </div>
    </Card>}

    {/* Payment history */}
    <Card data-testid="payment-history">
      <h2 className="text-lg font-semibold font-heading mb-3">سجل المدفوعات</h2>
      {transactions.length === 0 ? <p className="text-center py-6 text-[#4A5553] text-sm">لا توجد معاملات بعد.</p> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#E5E2DB] text-xs text-[#4A5553]">
              <th className="text-right py-2 px-2">التاريخ</th>
              <th className="text-right py-2 px-2">الباقة</th>
              <th className="text-right py-2 px-2">المبلغ</th>
              <th className="text-right py-2 px-2">الحالة</th>
              <th className="text-right py-2 px-2">المعرّف</th>
            </tr></thead>
            <tbody>
              {transactions.map(t => <tr key={t.id} className="border-b border-[#F0EFEB]" data-testid={`tx-${t.id}`}>
                <td className="py-2 px-2 text-xs">{new Date(t.created_at).toLocaleDateString("ar-EG")}</td>
                <td className="py-2 px-2">{t.plan_name_ar}</td>
                <td className="py-2 px-2 font-semibold">{fmtMoney(t.amount, t.currency)}</td>
                <td className="py-2 px-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${t.payment_status==="paid"?"bg-emerald-100 text-emerald-700":t.payment_status==="unpaid"?"bg-amber-100 text-amber-700":"bg-red-100 text-red-700"}`}>
                  {t.payment_status === "paid" ? "مدفوعة" : t.payment_status === "unpaid" ? "معلّقة" : t.payment_status}
                </span></td>
                <td className="py-2 px-2 text-[10px] font-mono text-[#4A5553]">{t.session_id?.slice(-10)}</td>
              </tr>)}
            </tbody>
          </table>
        </div>}
    </Card>

    {/* Invoices */}
    {invoices.length > 0 && <Card data-testid="invoices-list">
      <h2 className="text-lg font-semibold font-heading mb-3">الفواتير الرسمية</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {invoices.map(inv => <div key={inv.id} className="p-3 rounded-lg border border-[#E5E2DB] bg-white">
          <div className="flex justify-between items-start mb-2">
            <p className="font-mono text-[11px] text-[#4A5553]">{inv.id}</p>
            <p className="text-[10px] text-[#4A5553]">{new Date(inv.issued_at).toLocaleDateString("ar-EG")}</p>
          </div>
          <p className="font-semibold text-sm mb-1">{inv.plan_name_ar}</p>
          <p className="text-lg font-bold text-[#2F5233]">{fmtMoney(inv.amount, inv.currency)}</p>
          {inv.coupon_code && <p className="text-[10px] text-emerald-600 mt-1">كوبون: {inv.coupon_code} (-{fmtMoney(inv.savings, inv.currency)})</p>}
        </div>)}
      </div>
    </Card>}
  </div>;
}

// ============= /admin/pricing =============
function ForbiddenScreen() {
  return <div className="min-h-[50vh] flex items-center justify-center p-6" data-testid="no-access">
    <Card className="max-w-md text-center">
      <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center"><AlertCircle className="text-red-600" size={28}/></div>
      <h2 className="text-xl font-bold mb-2">صفحة محظورة</h2>
      <p className="text-sm text-[#4A5553] mb-4">هذه الصفحة مخصّصة لرئيس قسم الدمج فقط.</p>
      <Link to="/"><Btn>العودة للوحة</Btn></Link>
    </Card>
  </div>;
}

export function AdminPricingDashboard() {
  const [me, setMe] = useState(null);
  useEffect(() => { axios.get(`${API}/auth/me`).then(r => setMe(r.data)).catch(() => setMe({})); }, []);
  if (me === null) return <div className="p-8 text-center text-[#4A5553]">جارٍ التحقق...</div>;
  if (me?.account_type !== "inclusion_head") return <ForbiddenScreen/>;
  return <AdminPricingDashboardInner/>;
}

function AdminPricingDashboardInner() {
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [payments, setPayments] = useState(null);
  const [tab, setTab] = useState("plans");
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const reload = () => {
    axios.get(`${API}/pricing/plans`).then(r => setPlans(r.data));
    axios.get(`${API}/admin/coupons`).then(r => setCoupons(r.data));
    axios.get(`${API}/admin/payments`).then(r => setPayments(r.data));
  };
  useEffect(() => { reload(); }, []);

  const togglePlanActive = async (plan) => {
    await axios.patch(`${API}/admin/pricing/plans/${plan.id}`, { active: !plan.active });
    reload();
  };
  const deletePlan = async (plan) => {
    if (!window.confirm(`تعطيل خطة "${plan.name_ar}"؟ (لن تُحذف نهائياً)`)) return;
    await axios.delete(`${API}/admin/pricing/plans/${plan.id}`);
    reload();
  };
  const deleteCoupon = async (cp) => {
    if (!window.confirm(`تعطيل كوبون "${cp.code}"؟`)) return;
    await axios.delete(`${API}/admin/coupons/${cp.id}`);
    reload();
  };

  return <div className="space-y-5 fade-up" data-testid="admin-pricing-dashboard">
    <div>
      <div className="flex items-center gap-2 text-xs text-[#769FCD] font-semibold uppercase tracking-wider mb-1"><DollarSign size={14}/>إدارة الأسعار والاشتراكات</div>
      <h1 className="text-3xl font-bold font-heading">لوحة التسعير الإدارية</h1>
      <p className="text-sm text-[#4A5553] mt-1">إنشاء وتعديل الباقات، إدارة الكوبونات، ومتابعة المدفوعات.</p>
    </div>

    {/* KPIs */}
    {payments?.kpis && <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card className="p-3" data-testid="kpi-revenue"><p className="text-xs text-[#4A5553]">إجمالي الإيرادات</p><p className="text-2xl font-bold text-emerald-700">{fmtMoney(payments.kpis.total_revenue_sar, "sar")}</p></Card>
      <Card className="p-3" data-testid="kpi-paid"><p className="text-xs text-[#4A5553]">معاملات مدفوعة</p><p className="text-2xl font-bold">{payments.kpis.paid_count}</p></Card>
      <Card className="p-3" data-testid="kpi-subs"><p className="text-xs text-[#4A5553]">اشتراكات نشطة</p><p className="text-2xl font-bold text-[#527598]">{payments.kpis.active_subscriptions}</p></Card>
      <Card className="p-3" data-testid="kpi-pending"><p className="text-xs text-[#4A5553]">معاملات معلّقة</p><p className="text-2xl font-bold text-amber-700">{payments.kpis.pending_count}</p></Card>
    </div>}

    {/* Tabs */}
    <div className="flex gap-2 border-b border-[#E5E2DB]">
      {[["plans", "الباقات"], ["coupons", "الكوبونات"], ["payments", "المدفوعات"]].map(([v, l]) => <button key={v} onClick={()=>setTab(v)} data-testid={`adm-tab-${v}`}
        className={`px-4 py-2 text-sm transition ${tab===v?"border-b-2 border-[#2F5233] text-[#2F5233] font-semibold":"text-[#4A5553] hover:text-[#2F5233]"}`}>{l}</button>)}
    </div>

    {/* PLANS */}
    {tab === "plans" && <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">إدارة الباقات ({plans.length})</h2>
        <Btn onClick={()=>{ setEditingPlan(null); setShowPlanForm(true); }} data-testid="add-plan"><Plus size={14} className="inline ms-1"/>إضافة خطة</Btn>
      </div>
      {showPlanForm && <PlanForm initial={editingPlan} onClose={()=>setShowPlanForm(false)} onSaved={()=>{ setShowPlanForm(false); reload(); }}/>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm bg-white rounded-xl border border-[#E5E2DB]">
          <thead><tr className="bg-[#F9F8F6] text-xs text-[#4A5553]">
            <th className="text-right py-2 px-2">السلوغ</th>
            <th className="text-right py-2 px-2">الاسم</th>
            <th className="text-right py-2 px-2">النوع</th>
            <th className="text-right py-2 px-2">السعر</th>
            <th className="text-right py-2 px-2">الفئة</th>
            <th className="text-right py-2 px-2">الحالة</th>
            <th className="text-right py-2 px-2">إجراءات</th>
          </tr></thead>
          <tbody>
            {plans.map(p => <tr key={p.id} className="border-t border-[#F0EFEB]" data-testid={`adm-plan-${p.slug}`}>
              <td className="py-2 px-2 font-mono text-[10px]">{p.slug}</td>
              <td className="py-2 px-2">{p.name_ar}</td>
              <td className="py-2 px-2 text-xs">{p.type === "subscription" ? "اشتراك" : "دفعة واحدة"}{p.interval ? ` · ${intervalLabel(p.interval)}` : ""}</td>
              <td className="py-2 px-2 font-semibold">{fmtMoney(p.amount, p.currency)}</td>
              <td className="py-2 px-2 text-xs">{p.tier}</td>
              <td className="py-2 px-2"><button onClick={()=>togglePlanActive(p)} data-testid={`toggle-${p.slug}`} className={`text-[10px] px-2 py-0.5 rounded-full ${p.active?"bg-emerald-100 text-emerald-700":"bg-[#F0EFEB] text-[#4A5553]"}`}>{p.active?"نشط":"معطّل"}</button></td>
              <td className="py-2 px-2">
                <div className="flex gap-1">
                  <button onClick={()=>{ setEditingPlan(p); setShowPlanForm(true); }} data-testid={`edit-${p.slug}`} className="text-[#2F5233] hover:bg-[#F0EFEB] p-1 rounded"><Pencil size={12}/></button>
                  <button onClick={()=>deletePlan(p)} data-testid={`del-${p.slug}`} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={12}/></button>
                </div>
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </div>}

    {/* COUPONS */}
    {tab === "coupons" && <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">الكوبونات والخصومات ({coupons.length})</h2>
        <Btn onClick={()=>setShowCouponForm(true)} data-testid="add-coupon"><Plus size={14} className="inline ms-1"/>إضافة كوبون</Btn>
      </div>
      {showCouponForm && <CouponForm onClose={()=>setShowCouponForm(false)} onSaved={()=>{ setShowCouponForm(false); reload(); }} plans={plans}/>}
      {coupons.length === 0 ? <Card><p className="text-center py-6 text-[#4A5553] text-sm">لا توجد كوبونات بعد.</p></Card> :
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {coupons.map(cp => <div key={cp.id} className={`p-3 rounded-xl border-2 ${cp.active?"border-emerald-200 bg-emerald-50/30":"border-[#E5E2DB] bg-[#F9F8F6] opacity-70"}`} data-testid={`coupon-${cp.code}`}>
          <div className="flex justify-between items-start mb-1">
            <span className="font-mono font-bold text-base">{cp.code}</span>
            <button onClick={()=>deleteCoupon(cp)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={12}/></button>
          </div>
          <p className="text-2xl font-bold mb-1">{cp.type === "percent" ? `${cp.value}%` : fmtMoney(cp.value, "sar")} <span className="text-xs font-normal text-[#4A5553]">خصم</span></p>
          {cp.description && <p className="text-xs text-[#4A5553] mb-2">{cp.description}</p>}
          <div className="flex gap-2 text-[10px]">
            <span className="px-2 py-0.5 rounded-full bg-[#769FCD]/15 text-[#527598]">استخدم {cp.redeemed_count || 0}{cp.max_redemptions ? `/${cp.max_redemptions}` : ""}</span>
            {cp.applies_to?.length > 0 && <span className="px-2 py-0.5 rounded-full bg-[#E8A317]/15 text-[#A36F00]">{cp.applies_to.length} خطة</span>}
          </div>
        </div>)}
      </div>}
    </div>}

    {/* PAYMENTS */}
    {tab === "payments" && <div className="space-y-3">
      <h2 className="text-lg font-semibold">سجل المدفوعات الكامل ({payments?.transactions?.length || 0})</h2>
      <Card>
        {!payments || payments.transactions.length === 0 ? <p className="text-center py-6 text-[#4A5553] text-sm">لا توجد معاملات بعد.</p> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#E5E2DB] text-xs text-[#4A5553]">
              <th className="text-right py-2 px-2">التاريخ</th>
              <th className="text-right py-2 px-2">المستخدم</th>
              <th className="text-right py-2 px-2">الباقة</th>
              <th className="text-right py-2 px-2">المبلغ</th>
              <th className="text-right py-2 px-2">الكوبون</th>
              <th className="text-right py-2 px-2">الحالة</th>
            </tr></thead>
            <tbody>
              {payments.transactions.map(t => <tr key={t.id} className="border-b border-[#F0EFEB]" data-testid={`adm-tx-${t.id}`}>
                <td className="py-2 px-2 text-xs">{new Date(t.created_at).toLocaleDateString("ar-EG")}</td>
                <td className="py-2 px-2 text-xs">{t.user_email}</td>
                <td className="py-2 px-2">{t.plan_name_ar}</td>
                <td className="py-2 px-2 font-semibold">{fmtMoney(t.amount, t.currency)}</td>
                <td className="py-2 px-2 text-xs">{t.coupon_code || "—"}</td>
                <td className="py-2 px-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${t.payment_status==="paid"?"bg-emerald-100 text-emerald-700":"bg-amber-100 text-amber-700"}`}>{t.payment_status === "paid" ? "مدفوعة" : "معلّقة"}</span></td>
              </tr>)}
            </tbody>
          </table>
        </div>}
      </Card>
    </div>}
  </div>;
}

// ============= Forms =============
function PlanForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    slug: initial?.slug || "",
    name_ar: initial?.name_ar || "",
    name_en: initial?.name_en || "",
    type: initial?.type || "subscription",
    tier: initial?.tier || "parent_pro",
    interval: initial?.interval || "month",
    amount: initial?.amount ?? 0,
    currency: initial?.currency || "sar",
    features_ar: (initial?.features_ar || []).join("\n"),
    features_en: (initial?.features_en || []).join("\n"),
    audience: (initial?.audience || ["parent"]).join(","),
    active: initial?.active ?? true,
    popular: initial?.popular ?? false,
    sort_order: initial?.sort_order ?? 100,
    stripe_price_id: initial?.stripe_price_id || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        ...f, amount: parseFloat(f.amount), sort_order: parseInt(f.sort_order),
        interval: f.type === "subscription" ? f.interval : null,
        features_ar: f.features_ar.split("\n").map(s=>s.trim()).filter(Boolean),
        features_en: f.features_en.split("\n").map(s=>s.trim()).filter(Boolean),
        audience: f.audience.split(",").map(s=>s.trim()).filter(Boolean),
        stripe_price_id: f.stripe_price_id || null,
      };
      if (isEdit) {
        delete payload.slug; delete payload.type; delete payload.tier;
        await axios.patch(`${API}/admin/pricing/plans/${initial.id}`, payload);
      } else {
        await axios.post(`${API}/admin/pricing/plans`, payload);
      }
      onSaved();
    } catch (e) { alert(fmtErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return <Card data-testid="plan-form">
    <div className="flex justify-between items-center mb-3">
      <h3 className="text-lg font-semibold">{isEdit ? `تعديل: ${initial.name_ar}` : "إضافة خطة جديدة"}</h3>
      <button onClick={onClose}><X size={18}/></button>
    </div>
    <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {!isEdit && <Input placeholder="سلوغ (مثل parent_pro_v2)" value={f.slug} onChange={e=>setF({...f, slug:e.target.value.toLowerCase()})} required data-testid="pf-slug"/>}
      <Input placeholder="الاسم العربي" value={f.name_ar} onChange={e=>setF({...f, name_ar:e.target.value})} required data-testid="pf-name-ar"/>
      <Input placeholder="Name (English)" value={f.name_en} onChange={e=>setF({...f, name_en:e.target.value})} data-testid="pf-name-en"/>
      {!isEdit && <Select value={f.type} onChange={e=>setF({...f, type:e.target.value})} data-testid="pf-type">
        <option value="subscription">اشتراك دوري</option>
        <option value="one_time">دفعة واحدة</option>
      </Select>}
      {!isEdit && <Select value={f.tier} onChange={e=>setF({...f, tier:e.target.value})} data-testid="pf-tier">
        <option value="free">Free</option>
        <option value="parent_pro">Parent Pro</option>
        <option value="specialist">Specialist</option>
        <option value="school">School</option>
        <option value="center">Center</option>
        <option value="addon">Add-on</option>
        <option value="accreditation">Accreditation</option>
        <option value="training">Training</option>
      </Select>}
      {f.type === "subscription" && <Select value={f.interval} onChange={e=>setF({...f, interval:e.target.value})}>
        <option value="month">شهري</option>
        <option value="year">سنوي</option>
      </Select>}
      <Input type="number" step="0.01" min={0} placeholder="السعر" value={f.amount} onChange={e=>setF({...f, amount:e.target.value})} required data-testid="pf-amount"/>
      <Select value={f.currency} onChange={e=>setF({...f, currency:e.target.value})}>
        <option value="sar">SAR ر.س</option>
        <option value="usd">USD $</option>
        <option value="aed">AED د.إ</option>
      </Select>
      <Input placeholder="الجمهور (parent,specialist,school) — مفصولة بفواصل" value={f.audience} onChange={e=>setF({...f, audience:e.target.value})}/>
      <Input type="number" placeholder="ترتيب العرض" value={f.sort_order} onChange={e=>setF({...f, sort_order:e.target.value})}/>
      <Input placeholder="Stripe Price ID (اختياري للاشتراكات)" value={f.stripe_price_id} onChange={e=>setF({...f, stripe_price_id:e.target.value})} className="md:col-span-2"/>
      <textarea className="md:col-span-2 px-3 py-2 rounded-lg border border-[#E5E2DB]" rows={4} placeholder="المميزات بالعربية (سطر لكل ميزة)" value={f.features_ar} onChange={e=>setF({...f, features_ar:e.target.value})}/>
      <textarea className="md:col-span-2 px-3 py-2 rounded-lg border border-[#E5E2DB]" rows={4} placeholder="Features in English (one per line)" value={f.features_en} onChange={e=>setF({...f, features_en:e.target.value})}/>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.popular} onChange={e=>setF({...f, popular:e.target.checked})}/> الأكثر شيوعاً</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={e=>setF({...f, active:e.target.checked})}/> نشط</label>
      <div className="md:col-span-2 flex gap-2"><Btn type="submit" disabled={busy} data-testid="pf-save">{busy?"...":"حفظ"}</Btn><Btn variant="outline" type="button" onClick={onClose}>إلغاء</Btn></div>
    </form>
  </Card>;
}

function CouponForm({ onClose, onSaved, plans }) {
  const [f, setF] = useState({
    code: "", type: "percent", value: 10, applies_to: [],
    max_redemptions: "", description: "", valid_from: "", valid_until: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        ...f, value: parseFloat(f.value),
        max_redemptions: f.max_redemptions ? parseInt(f.max_redemptions) : null,
        valid_from: f.valid_from || null, valid_until: f.valid_until || null,
        active: true,
      };
      await axios.post(`${API}/admin/coupons`, payload);
      onSaved();
    } catch (e) { alert(fmtErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const togglePlan = (pid) => setF(s => ({
    ...s, applies_to: s.applies_to.includes(pid) ? s.applies_to.filter(x=>x!==pid) : [...s.applies_to, pid],
  }));
  return <Card data-testid="coupon-form">
    <div className="flex justify-between items-center mb-3">
      <h3 className="text-lg font-semibold">إضافة كوبون</h3>
      <button onClick={onClose}><X size={18}/></button>
    </div>
    <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Input placeholder="الكود (مثل WELCOME20)" value={f.code} onChange={e=>setF({...f, code:e.target.value.toUpperCase()})} required data-testid="cf-code"/>
      <Select value={f.type} onChange={e=>setF({...f, type:e.target.value})}>
        <option value="percent">نسبة مئوية %</option>
        <option value="fixed">قيمة ثابتة</option>
      </Select>
      <Input type="number" step="0.01" min={0} placeholder="القيمة" value={f.value} onChange={e=>setF({...f, value:e.target.value})} required data-testid="cf-value"/>
      <Input type="number" min={0} placeholder="حد أقصى للاستخدام (اختياري)" value={f.max_redemptions} onChange={e=>setF({...f, max_redemptions:e.target.value})}/>
      <Input type="date" placeholder="من" value={f.valid_from?.slice(0,10) || ""} onChange={e=>setF({...f, valid_from:e.target.value?new Date(e.target.value).toISOString():""})}/>
      <Input type="date" placeholder="إلى" value={f.valid_until?.slice(0,10) || ""} onChange={e=>setF({...f, valid_until:e.target.value?new Date(e.target.value).toISOString():""})}/>
      <Input placeholder="الوصف" value={f.description} onChange={e=>setF({...f, description:e.target.value})} className="md:col-span-2"/>
      <div className="md:col-span-2">
        <p className="text-xs text-[#4A5553] mb-1">الباقات المنطبق عليها (فارغ = كل الباقات)</p>
        <div className="flex flex-wrap gap-1.5">
          {plans.map(p => <button key={p.id} type="button" onClick={()=>togglePlan(p.id)}
            className={`text-[10px] px-2 py-0.5 rounded-full border transition ${f.applies_to.includes(p.id)?"bg-[#2F5233] text-white border-[#2F5233]":"bg-white text-[#4A5553] border-[#E5E2DB]"}`}>{p.name_ar}</button>)}
        </div>
      </div>
      <div className="md:col-span-2 flex gap-2"><Btn type="submit" disabled={busy} data-testid="cf-save">{busy?"...":"إنشاء"}</Btn><Btn variant="outline" type="button" onClick={onClose}>إلغاء</Btn></div>
    </form>
  </Card>;
}
